import {Component, DestroyRef, Input, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {TopEcusListDialogComponent} from './top-ecus-list-dialog.component';
import {DashboardStatisticsService} from '../../../dashboard-analytics/services/dashboard-statistics.service';
import {EcuBuildStats} from '../../../dashboard-analytics/models/dashboard-statistics.model';

const DEFAULT_LIMIT = 5;

@Component({
  selector: 'app-top-ecus-card',
  templateUrl: './top-ecus-card.component.html',
  styleUrls: ['./top-ecus-card.component.css']
})
export class TopEcusCardComponent implements OnInit {

  @Input({required: true}) type!: 'missing' | 'partial';

  private readonly statsService = inject(DashboardStatisticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  readonly ecus = signal<EcuBuildStats[]>([]);
  readonly isLoading = signal<boolean>(true);

  readonly rows = computed(() => {
    const list = [...this.ecus()].sort((a, b) => b.count - a.count);
    const max = list.length > 0 ? list[0].count : 1;
    return list.map(e => ({...e, barPct: Math.round((e.count / max) * 100)}));
  });

  readonly title = computed(() => this.type === 'missing' ? 'Top Missing ECUs' : 'Top Partial ECUs');
  readonly subtitle = computed(() => this.type === 'missing'
    ? 'ECU builds with the most missing Pure Originals'
    : 'ECU builds with the most partial Pure Originals');
  readonly accentClass = computed(() => this.type === 'missing' ? 'red' : 'amber');

  ngOnInit(): void {
    this.fetch(DEFAULT_LIMIT);
  }

  openEcu(ecuBuild: string): void {
    if (ecuBuild == null) return;
    this.router.navigate(['/app/requested-tunings/requested-tunings-tables'], {
      queryParams: {type: this.type, ecuBuild, source: 'dashboard'}
    });
  }

  showAll(): void {
    this.dialog.open(TopEcusListDialogComponent, {
      data: {type: this.type}, width: '720px', maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100dvh - 48px)', ariaLabelledBy: 'top-ecus-dialog-title', autoFocus: '.close-button'
    });
  }

  private fetch(limit: number): void {
    this.isLoading.set(true);
    const req$ = this.type === 'missing'
      ? this.statsService.getMissingMatchingByEcuBuild(limit)
      : this.statsService.getPartialMatchingByEcuBuild(limit);

    req$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.ecus.set(list ?? []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }
}
